/**
 * ExcelBridge — Singleton service for Excel refresh callbacks.
 *
 * INTENTIONAL SINGLETON: Office.js operates outside React's render cycle.
 * Excel Add-In event handlers need stable references to FME callbacks that
 * survive React re-renders. This is an explicit exception to the no-globals rule.
 *
 * Phase 3B: stores queryClient + scenarioId for reload operations via query
 * invalidation. Keeps Excel-specific callbacks (refreshTsSheet etc.) as direct
 * function refs since they call Office.js APIs outside React.
 */

import type { QueryClient } from '@tanstack/react-query';
import { SCENARIO_TREE_KEY } from '../../../shared/hooks/queries/useScenarioTree';
import { SECTION_DATA_KEY } from '../../../shared/hooks/queries/useSectionLoader';

type CallbackFn = (...args: any[]) => any;

class ExcelBridge {
    private static instance: ExcelBridge;

    private queryClient: QueryClient | null = null;
    private scenarioId: number = 0;

    // Excel-specific callbacks (Office.js APIs, not replaceable by query invalidation)
    private recreateExcelInput: CallbackFn | null = null;
    private refreshTsSheet: CallbackFn | null = null;
    private refreshCaSheet: CallbackFn | null = null;
    private refreshInputSheet: CallbackFn | null = null;

    // Sensitivity reload still needs a callback since it involves scenario/sensitivity
    // state transitions that go beyond simple query invalidation
    private reloadSensitivityFn: CallbackFn | null = null;

    private constructor() {}

    static getInstance(): ExcelBridge {
        if (!ExcelBridge.instance) {
            ExcelBridge.instance = new ExcelBridge();
        }
        return ExcelBridge.instance;
    }

    registerQueryClient(qc: QueryClient, scenarioId: number) {
        this.queryClient = qc;
        this.scenarioId = scenarioId;
    }

    registerCallbacks(callbacks: {
        recreateExcelInput: CallbackFn;
        refreshTsSheet: CallbackFn;
        refreshCaSheet: CallbackFn;
        refreshInputSheet: CallbackFn;
        reloadSensitivity: CallbackFn;
    }) {
        this.recreateExcelInput = callbacks.recreateExcelInput;
        this.refreshTsSheet = callbacks.refreshTsSheet;
        this.refreshCaSheet = callbacks.refreshCaSheet;
        this.refreshInputSheet = callbacks.refreshInputSheet;
        this.reloadSensitivityFn = callbacks.reloadSensitivity;
    }

    /** Reset all callbacks and queryClient to null. For test isolation only. */
    reset(): void {
        this.queryClient = null;
        this.scenarioId = 0;
        this.recreateExcelInput = null;
        this.refreshTsSheet = null;
        this.refreshCaSheet = null;
        this.refreshInputSheet = null;
        this.reloadSensitivityFn = null;
    }

    /**
     * Invalidate scenario-tree cache to trigger re-render.
     * Replaces the old reloadState() callback.
     */
    invalidateTree(): void {
        if (this.queryClient && this.scenarioId > 0) {
            this.queryClient.invalidateQueries({
                queryKey: [SCENARIO_TREE_KEY, this.scenarioId],
            });
        }
    }

    /**
     * Invalidate section and scenario-tree caches to reload model data.
     * Replaces the old reloadStateFromApi() callback.
     */
    reloadStateFromApi(): void {
        if (this.queryClient && this.scenarioId > 0) {
            this.queryClient.invalidateQueries({
                queryKey: [SECTION_DATA_KEY, this.scenarioId],
            });
            this.queryClient.invalidateQueries({
                queryKey: [SCENARIO_TREE_KEY, this.scenarioId],
            });
        }
    }

    /**
     * Invalidate output data.
     * Replaces the old reloadOutputStateFromApi() callback.
     */
    reloadOutputStateFromApi(): void {
        if (this.queryClient && this.scenarioId > 0) {
            this.queryClient.invalidateQueries({
                queryKey: [SCENARIO_TREE_KEY, this.scenarioId],
            });
        }
    }

    getRecreateExcelInput(): CallbackFn | null { return this.recreateExcelInput; }
    getRefreshTsSheet(): CallbackFn | null { return this.refreshTsSheet; }
    getRefreshCaSheet(): CallbackFn | null { return this.refreshCaSheet; }
    getRefreshInputSheet(): CallbackFn | null { return this.refreshInputSheet; }
    getReloadSensitivity(): CallbackFn | null { return this.reloadSensitivityFn; }
}

const excelBridge = ExcelBridge.getInstance();
export default excelBridge;
